import React from "react";
import {
    ApiQuery,
    ApiQueryExample,
    FilterMetadata,
    PaginationState,
    SortQuery,
} from "~/modules/filters/types";
import { EmptyState } from "~/modules/filters/components/EmptyState";
import { FiltersLayout } from "~/modules/filters/components";
import { ExportType } from "~/modules/downloads/types";
import { FadeIn } from "~/components/FadeIn";
import { LoadingSpinner } from "~/components/LoadingSpinner";
import { ApiSortInput } from "./ApiSortInput";

// // // //

// TODO - Annotate this component
// -> let's add another page to sanity-check some of these patterns a bit
// -> let's do it for Protocols page, should be a small lift to experiment with
// TODO - update these props to use consistent terminology + language -> loading vs fetching, etc.
export function ListPageLayout(props: {
    children: React.ReactNode;
    appliedQuery: {
        value: ApiQuery;
        fetching: boolean;
        items: any[];
        pagination: PaginationState;
        onChange: (updatedQuery: ApiQuery) => void;
    };
    filterMetadata: {
        loading: boolean;
        items: FilterMetadata[];
        queryExamples: ApiQueryExample[];
    };
    internalQuery: {
        value: ApiQuery;
        fetching: boolean;
        resultCount: number;
        onChange: (updatedQuery: ApiQuery) => void;
    };
    // TODO - rename this prop
    export?: {
        // TODO - pass in loading state here
        onExport: (exportType: ExportType) => void;
    };
    onResetQuery: () => void;
    emptyStateMessage: string;
    apiDocsBaseUrl?: string;
    enableProSearch?: boolean;
    enableBasicFilters?: boolean;
    enableClearFilters?: boolean;
    enableSorting?: boolean;
    sharing?: {
        enabled: boolean;
        url: string;
    };
    title?: string;
}) {
    const {
        children,
        appliedQuery,
        internalQuery,
        filterMetadata,
        onResetQuery,
        emptyStateMessage,
        apiDocsBaseUrl,
        enableProSearch,
        enableSorting = false,
        enableBasicFilters = true,
        title,
        sharing = {
            enabled: false,
            url: "",
        },
    } = props;
    const { items } = appliedQuery;

    // Define a flag to determine whether or not to render the <FiltersLayout> component
    const showFiltersLayout = enableBasicFilters || enableProSearch;
    const enableClearFilters = props.enableClearFilters || showFiltersLayout; // TODO - clean this up

    return (
        <React.Fragment>
            {/* NOTE - we should use a LoadingBoundary here instead of React.Fragment */}
            {/* LoadingBoundary works on FULL PAGE layouts, but not inline layouts */}
            {/* We should re-activate this later when we start rolling out dedicated list pages  */}
            {showFiltersLayout && (
                <div className="flex w-full">
                    <FiltersLayout
                        title={title}
                        onResetQuery={onResetQuery}
                        query={appliedQuery.value}
                        apiDocsBaseUrl={apiDocsBaseUrl}
                        enableProSearch={enableProSearch}
                        enableBasicFilters={enableBasicFilters}
                        enableClearFilters={enableClearFilters}
                        sharing={sharing}
                        // QUESTION - do we need to keep "status" here? --> PROBABLY, yes. -> BUT, it should be renamed
                        // TODO - pass in DEFAULT_FILTERS here??
                        status={{
                            loading: appliedQuery.fetching,
                            resultCount: appliedQuery.pagination.resultCount,
                        }}
                        filterMetadata={filterMetadata.items}
                        queryExamples={filterMetadata.queryExamples}
                        onChange={(updatedApiQuery: ApiQuery) => {
                            appliedQuery.onChange({
                                ...updatedApiQuery,
                                // Ensures currently applied sort behavior is retained
                                sort: {
                                    ...appliedQuery.value.sort,
                                },
                                // Ensures pagination state is reset
                                pagination: {
                                    ...appliedQuery.pagination,
                                    page: 1,
                                },
                            });
                        }}
                        // TODO - pass in export loading state, should have some transmission
                        onExport={
                            !props.export
                                ? undefined
                                : (exportType: ExportType) => {
                                      // Short-circuit if props.export isn't defined
                                      // (Just here for TypeScript safety)
                                      if (!props.export) {
                                          return;
                                      }

                                      // Execute export
                                      props.export.onExport(exportType);
                                  }
                        }
                        internalQuery={{
                            value: internalQuery.value,
                            loading: internalQuery.fetching,
                            resultCount: internalQuery.resultCount,
                            onChange: (updatedQuery: ApiQuery) => {
                                internalQuery.onChange(updatedQuery);
                            },
                        }}
                        appliedQuery={{
                            loading: appliedQuery.fetching,
                        }}
                    />
                </div>
            )}

            {/* NOTE - this works on FULL PAGE layouts, but not inline layouts */}
            {/* We should re-activate this later when we start rolling out dedicated list pages  */}
            {appliedQuery.fetching && items.length === 0 && (
                <div className="flex items-center justify-center w-full h-64">
                    <FadeIn>
                        <LoadingSpinner size={10} />
                    </FadeIn>
                </div>
            )}

            {!appliedQuery.fetching && items.length === 0 && (
                <EmptyState
                    message={emptyStateMessage}
                    onClear={() => {
                        onResetQuery();
                    }}
                />
            )}

            {/* Render the table when items are present */}
            {items.length > 0 && (
                <React.Fragment>
                    {enableSorting && (
                        <ApiSortInput
                            className="w-full justify-end mb-4"
                            loading={appliedQuery.fetching}
                            filterMetadata={filterMetadata.items}
                            sortInput={appliedQuery.value.sort}
                            onChange={(updatedSort: SortQuery) => {
                                appliedQuery.onChange({
                                    ...appliedQuery.value,
                                    sort: updatedSort,
                                });
                            }}
                        />
                    )}
                    {children}
                </React.Fragment>
            )}
        </React.Fragment>
    );
}
